'use strict'

import * as React from 'react'
import { AutoSizer, Table, Column } from 'react-virtualized'
import { getOrgCodeColumnIndex } from '../extra/helpers'

/** テーブルレイアウト定数（A3-001） */
const TABLE_HEADER_HEIGHT = 40
const TABLE_ROW_HEIGHT = 40
const TABLE_ROW_HEIGHT_WITH_ERROR = 72
const TABLE_MAX_HEIGHT = 600

/** 組織階層テーブル（仮想化）。API master/processed を表示し、階層に応じて緑・青と < > を付与する。 DepInfoHierarchySetting 専用。元は org/HierarchyInfo.js を参照。 */

/** 空セル判定（'', '-', null, undefined は空とみなす） */
function isEmptyCell(v) {
  if (v == null) return true
  const s = String(v).trim()
  return s === '' || s === '-'
}

/** ドリルダウン表示用の値か（"(N)組織名" 形式なら true）。この場合は title 扱いにせず色・矢印を付ける */
function isDrilldownValue(v) {
  if (v == null) return false
  const s = String(v).trim()
  return s.length > 0 && s.startsWith('(')
}

/**
 * @param {Object} props
 * @param {string[]} props.headers - 表示列ヘッダー（所属組織1（グラフ用）...）
 * @param {string[][]} props.masterList - 2D（先頭行＝ヘッダー）。rowGetter のデータ源
 * @param {string[][]} props.processedList - 2D（先頭行＝ヘッダー）。表示値・hasUpper/hasLower 判定に使用
 * @param {number[]} props.indexList - 各表示列の列インデックス（グラフ用列）
 * @param {string} [props.targetStartDate] - 期間キー（Column key 用）
 * @param {number} [props.orgCodeColumnIndex] - 組織コード列インデックス（processed 内）
 * @param {boolean} [props.showDrilldownSymbols=true] - False のとき < > を表示しない（最新期間コピー・マスタ表示用）. Default is `true`
 * @param {Object<string, Object<number | string, string>>} [props.rowErrors] - 行別エラー { [rowIndex]: { [columnIndex]:
 *   message } }（組織一覧未登録等）。組織コード列(6)のメッセージを表示
 */
export function HierarchyTable(props) {
  const {
    headers,
    masterList,
    indexList,
    processedList,
    targetStartDate,
    orgCodeColumnIndex,
    showDrilldownSymbols = true,
    rowErrors = {}
  } = props

  const tableHeaderHeight = TABLE_HEADER_HEIGHT
  const tableRowHeight = TABLE_ROW_HEIGHT
  const tableRowHeightWithError = TABLE_ROW_HEIGHT_WITH_ERROR
  const maxTableHeight = TABLE_MAX_HEIGHT

  const [scrollbarWidth, setScrollbarWidth] = React.useState(19)
  const tableRef = React.useRef(null)

  const rowHeights = React.useMemo(() => {
    const count = Math.max(0, masterList.length - 1)
    const heights = []
    for (let i = 0; i < count; i++) {
      const hasError = rowErrors[String(i)] && Object.keys(rowErrors[String(i)]).length > 0
      heights.push(hasError ? tableRowHeightWithError : tableRowHeight)
    }
    return heights
  }, [masterList.length, rowErrors])

  const tableBodyHeight = React.useMemo(() => {
    const total = rowHeights.reduce((sum, h) => sum + h, 0)
    return Math.min(tableHeaderHeight + total, maxTableHeight)
  }, [rowHeights])

  React.useEffect(() => {
    if (tableRef.current) {
      tableRef.current.scrollToRow(0)
    }
  }, [masterList])

  React.useEffect(() => {
    if (tableRef.current && typeof tableRef.current.recomputeRowHeights === 'function') {
      tableRef.current.recomputeRowHeights(0)
    }
  }, [rowErrors, rowHeights])

  React.useEffect(() => {
    const getScrollbarWidth = () => window.innerWidth - document.documentElement.clientWidth
    setScrollbarWidth(getScrollbarWidth())
    return () => setScrollbarWidth(0)
  }, [])

  const headerRenderer = ({ columnIndex }) => {
    const header = headers[columnIndex]
    const dataColIndex = indexList[columnIndex]
    let departmentCount = 0
    if (dataColIndex >= 0 && Array.isArray(masterList) && masterList.length > 1) {
      masterList.slice(1).forEach((row) => {
        const value = row[dataColIndex]
        if (isEmptyCell(value) || isDrilldownValue(value)) return
        const hasLowerLevel = indexList.slice(columnIndex + 1).some((idx) => idx >= 0 && !isEmptyCell(row[idx]))
        if (!hasLowerLevel) {
          departmentCount++
        }
      })
    }
    return (
      <div className="flex px-4">
        <span className="head-title">
          {header.replace('（グラフ用）', '')}
          <span className="pl-2">[{departmentCount}]</span>
        </span>
      </div>
    )
  }

  const cellRenderer = ({ rowData, rowIndex, columnIndex }) => {
    const dataRowIndex = rowIndex + 1
    if (!rowData) return null

    const dataColIndex = indexList[columnIndex]
    if (dataColIndex === undefined || dataColIndex < 0) return null

    const procRow = processedList[dataRowIndex] || []
    const pCol = processedList.length !== 0 ? procRow[dataColIndex] : ''
    const mCol = rowData[dataColIndex]

    const isTitleCell = !isEmptyCell(mCol) && !isDrilldownValue(mCol)
    const textCell = (text) => (
      <div className="truncate min-w-0" title={text != null && String(text).trim() !== '' ? String(text).trim() : ''}>
        {text ?? ''}
      </div>
    )

    const rowErr = rowErrors[String(rowIndex)] || {}
    const cellErrForCol = rowErr[dataColIndex] ?? rowErr[String(dataColIndex)]
    const hasCellError = Boolean(cellErrForCol)

    if (isTitleCell) {
      if (dataColIndex === orgCodeColumnIndex) {
        const cellErr = rowErr[6] ?? rowErr['6']
        const hasCodeError = Boolean(cellErr)
        return hasCodeError ? (
          <div className="bg-red-50 org-code-error-wrapper">
            <div className="title min-w-0 static bg-transparent border-none p-0">{textCell(pCol)}</div>
            <span className="block text-caption-2 red--text mt-0.5">{cellErr}</span>
          </div>
        ) : (
          <div className="title min-w-0">{textCell(pCol)}</div>
        )
      }

      // 組織名列: エラー時は組織コード列と同じレイアウト（上揃え・padding 8px 16px）
      return hasCellError ? (
        <div className="bg-red-50 org-name-error-wrapper min-w-0 w-full h-full flex flex-col justify-start">
          <div className="title min-w-0 static bg-transparent border-none p-0 flex-shrink-0">{textCell(pCol)}</div>
          <span className="block text-caption-2 red--text mt-0.5 flex-shrink-0">{cellErrForCol}</span>
        </div>
      ) : (
        <div className="title min-w-0">{textCell(pCol)}</div>
      )
    }

    const hasUpperHierarchy = indexList.slice(0, columnIndex).some((idx) => idx >= 0 && !isEmptyCell(procRow[idx]))
    const hasLowerHierarchy = indexList.slice(columnIndex + 1).some((idx) => idx >= 0 && !isEmptyCell(procRow[idx]))

    // 連続する同一ドリルダウン値の「先頭列」を探す（run start）
    // run start に上位階層がなければ drilldown span（緑）、あれば intermediate fill（青）
    const currentVal = (pCol ?? '').toString().trim()
    let runStart = columnIndex
    if (currentVal !== '' && currentVal !== '-') {
      while (runStart > 0) {
        const prevIdx = indexList[runStart - 1]
        const prevV = prevIdx >= 0 ? (procRow[prevIdx] ?? '').toString().trim() : ''
        if (prevV === currentVal) {
          runStart--
        } else {
          break
        }
      }
    }
    const isSameValueRun = runStart < columnIndex
    const runStartHasUpper = indexList.slice(0, runStart).some((idx) => idx >= 0 && !isEmptyCell(procRow[idx]))

    const isGreen =
      hasLowerHierarchy && ((!hasUpperHierarchy && !isSameValueRun) || (isSameValueRun && !runStartHasUpper))
    const isBlue = hasUpperHierarchy && hasLowerHierarchy && !isGreen
    const cellClassName = isGreen ? 'green--text' : isBlue ? 'blue3--text' : ''

    if (dataColIndex === orgCodeColumnIndex) {
      const cellErr = rowErr[6] ?? rowErr['6']
      const hasCodeError = Boolean(cellErr)
      return hasCodeError ? (
        <div className="bg-red-50 org-code-error-wrapper">
          <div className="flex department-name min-w-0">{textCell(pCol)}</div>
          <span className="block text-caption-2 red--text mt-0.5">{cellErr}</span>
        </div>
      ) : (
        <div className="min-w-0">
          <div className="flex department-name min-w-0">{textCell(pCol)}</div>
        </div>
      )
    }

    // 組織名列のエラー表示（組織コード列と同じ上揃え・paddingで表示）
    const errorWrapper = (content) =>
      hasCellError ? (
        <div className="bg-red-50 org-name-error-wrapper min-w-0 w-full h-full min-h-[52px] flex flex-col justify-start">
          {content}
          <span className="block text-caption-2 red--text mt-0.5 flex-shrink-0">{cellErrForCol}</span>
        </div>
      ) : (
        content
      )

    if (!showDrilldownSymbols) {
      return errorWrapper(<div className={`flex department-name min-w-0 ${cellClassName}`}>{textCell(pCol)}</div>)
    }

    return errorWrapper(
      <div className={`flex department-name min-w-0 ${cellClassName}`}>
        {isGreen && (
          <>
            <div className="text-center flex-shrink-0" />
            {textCell(pCol)}
            <div className="flex-shrink-0">{pCol !== '-' ? '<' : ''}</div>
          </>
        )}
        {isBlue && (
          <>
            <div className="text-center flex-shrink-0">{pCol !== '-' ? '>' : ''}</div>
            {textCell(pCol)}
          </>
        )}
        {!isGreen && !isBlue && textCell(pCol)}
      </div>
    )
  }

  const _rowGetter = ({ index }) => masterList[index + 1] || []

  return (
    <div className="virtualized-table hierarchy-table w-full" style={{ height: `${tableBodyHeight}px` }}>
      <AutoSizer>
        {({ width, height }) => {
          const totalWidth = width - scrollbarWidth
          const orgCodeColWidth = Math.min(180, Math.max(160, Math.floor(totalWidth / 7)))
          const equalWidth = Math.floor((totalWidth - orgCodeColWidth) / headers.length)
          const lastColumnWidth = totalWidth - equalWidth * headers.length

          const rowErrorsKey = Object.keys(rowErrors).sort().join(',')
          return (
            <Table
              key={`hierarchy-${targetStartDate ?? ''}-${rowErrorsKey}`}
              ref={tableRef}
              width={width}
              height={height}
              headerHeight={tableHeaderHeight}
              rowHeight={({ index }) => rowHeights[index] ?? tableRowHeight}
              rowCount={masterList.length - 1}
              rowGetter={_rowGetter}
              overscanRowCount={10}
              rowClassName="text-caption-2 text-nowrap"
            >
              {headers.map((_, columnIndex) => (
                <Column
                  width={equalWidth}
                  key={`${targetStartDate ?? ''}${columnIndex}`}
                  dataKey={`col-${columnIndex}`}
                  headerRenderer={() => headerRenderer({ columnIndex })}
                  cellRenderer={({ rowData, rowIndex }) => cellRenderer({ rowData, rowIndex, columnIndex })}
                />
              ))}
              <Column
                width={lastColumnWidth}
                key="col-orgCode"
                dataKey="orgCode"
                headerRenderer={() => (
                  <div className="flex px-4">
                    <span className="head-title">組織コード</span>
                  </div>
                )}
                cellRenderer={({ rowIndex }) => {
                  const row = processedList[rowIndex + 1] || []
                  const headerRow = processedList[0] || []
                  const idx =
                    typeof orgCodeColumnIndex === 'number' && orgCodeColumnIndex >= 0
                      ? orgCodeColumnIndex
                      : getOrgCodeColumnIndex(headerRow)
                  const orgCode = idx >= 0 && row.length > 0 ? (row[idx] ?? '') : ''
                  const codeStr = (orgCode ?? '').toString().trim()
                  const rowErr = rowErrors[String(rowIndex)] || {}
                  const cellErr = rowErr[6] ?? rowErr['6']
                  const hasError = Boolean(cellErr)
                  return hasError ? (
                    <div title={codeStr} className="bg-red-50 org-code-error-wrapper">
                      <div className="truncate">{orgCode}</div>
                      <span className="block text-caption-2 red--text mt-0.5">{cellErr}</span>
                    </div>
                  ) : (
                    <div title={codeStr} className="min-w-0 px-4">
                      <div className="truncate">{orgCode}</div>
                    </div>
                  )
                }}
              />
            </Table>
          )
        }}
      </AutoSizer>
    </div>
  )
}

export default HierarchyTable
